import { useDeposits } from '@/hooks/useDeposits';
import { useDepositAccountStatus } from '@/hooks/useDepositAccountStatus';
import { DepositRow } from '@/components/deposits/DepositRow';
import { PartialDepositSection } from '@/components/deposits/PartialDepositSection';
import DepositAccountManager from '@/components/deposits/DepositAccountManager';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { OnboardingWizardContainer, type OnboardingWizardHandle } from '@/components/deposits/onboarding/OnboardingWizardContainer';
import { DepositsIntro } from '@/components/deposits/DepositsIntro';
import { useAuth } from '@/hooks/useAuth';
import { useLoginModal } from '@/contexts/LoginModalContext';
import { PaidPlanGate } from '@/components/PaidPlanGate';

function DepositsContent() {
  const {
    queue,
    loading,
    error,
    queueVersion,
    refresh,
    confirm,
    undo,
    reject,
    restore,
    loadManualCandidates,
    rescueRevalidate,
    rescueConfirm,
  } = useDeposits();
  const { status: accountStatus, refresh: refreshAccountStatus } = useDepositAccountStatus();
  const { user } = useAuth();
  const { openLoginModal } = useLoginModal();
  const wizardRef = useRef<OnboardingWizardHandle>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const targetMatchId = searchParams.get('match');
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const lastHandledMatchIdRef = useRef<string | null>(null);

  // Task 5: /deposits?match={matchId} 딥링크 처리 — 스크롤+하이라이트 트리거만.
  // 정리(3초 타이머+URL 삭제)는 별도 effect로 분리(아래) — queueVersion 변화가 타이머를
  // 죽여 ?match= 파라미터가 영구 잔류하는 문제 방지(리뷰 Critical).
  useEffect(() => {
    if (!targetMatchId || targetMatchId === lastHandledMatchIdRef.current || loading) return;
    const id = Number(targetMatchId);
    const el = document.getElementById(`deposit-row-${id}`);
    if (!el) return;                       // 큐에 없으면 no-op(다음 refresh에서 재시도)
    lastHandledMatchIdRef.current = targetMatchId;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightId(id);
  }, [targetMatchId, loading, queueVersion]);

  // 하이라이트 자동 해제(3초) — 트리거 effect와 분리: queueVersion 변화(확정/되돌림 등)가
  // 정리 타이머를 죽여 ?match= 파라미터가 영구 잔류하는 것을 방지(리뷰 Critical).
  useEffect(() => {
    if (highlightId === null) return;
    const t = setTimeout(() => {
      setHighlightId(null);
      setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('match'); return n; }, { replace: true });
    }, 3000);
    return () => clearTimeout(t);
  }, [highlightId, setSearchParams]);

  const handleAccountChanged = useCallback(() => {
    void refresh();
    void refreshAccountStatus();
  }, [refresh, refreshAccountStatus]);
  // /deposits는 라우트 가드가 없어 비로그인도 진입 가능 — 온보딩 RPC는 authenticated 전용이라
  // 비로그인 CTA는 위저드 대신 로그인 모달을 연다.
  const handleStartOnboarding = useCallback(() => {
    if (!user) {
      openLoginModal();
      return;
    }
    wizardRef.current?.open();
  }, [user, openLoginModal]);

  // 실시간 매칭/확정으로 큐가 바뀌면 계좌 상태도 재조회 — provisional→confirmed 승격 UI 반영.
  useEffect(() => { void refreshAccountStatus(); }, [queueVersion, refreshAccountStatus]);

  // 인트로(랜딩)가 노출되는 동안엔 헤더의 설명문·연동 CTA가 인트로와 중복되므로 숨긴다.
  const queueEmpty =
    queue.actionNeeded.length === 0 &&
    queue.excluded.length === 0 &&
    queue.confirmed.length === 0;
  const introVisible =
    !loading && !error && queueEmpty &&
    (accountStatus === 'unlinked' || accountStatus === 'in_progress');

  return (
    <div className="min-h-screen bg-slate-50 p-4 text-slate-900 md:p-6 lg:p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        {/* 헤더 — 페이지 제목은 상단 nav('입금매칭')가 대신하므로 생략. 설명(좌) + 액션(우)만. */}
        {(!introVisible || accountStatus === 'in_progress') && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {!introVisible ? (
              <div className="space-y-0.5">
                <p className="break-keep text-sm leading-relaxed text-slate-600">
                  입금이 오면 <strong className="font-medium text-slate-900">정산서 화면에서 먼저 자동 매칭</strong>됩니다.
                </p>
                {/* '매칭되지 않은'은 부정확 — actionNeeded엔 자동 매칭된 미확정 건도 포함(확인 대기 큐 의미) */}
                <p className="break-keep text-sm leading-relaxed text-slate-500">
                  확인이 필요한 입금만 이곳에 모여요 — 주문을 확인하고{' '}
                  <strong className="font-medium text-slate-700">'입금 완료'</strong>로 처리할 수 있습니다.
                </p>
              </div>
            ) : (
              <div aria-hidden />
            )}
            <div className="flex shrink-0 items-center gap-2">
              {!introVisible && (accountStatus === 'unlinked' || accountStatus === 'in_progress') && (
                <Button
                  className="min-h-11 bg-slate-900 text-white hover:bg-slate-800"
                  onClick={handleStartOnboarding}
                >
                  {accountStatus === 'in_progress' ? '계좌 연동 계속' : '계좌 연동'}
                </Button>
              )}
              {(accountStatus === 'in_progress' || accountStatus === 'linked' || accountStatus === 'unknown') && (
                <DepositAccountManager onChanged={handleAccountChanged} />
              )}
            </div>
          </div>
        )}

        {/* 로딩 */}
        {loading && (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-20 w-full rounded-2xl" />
            ))}
          </div>
        )}

        {/* 에러 */}
        {!loading && error && (
          <div className="rounded-2xl bg-white p-6 text-center shadow-sm ring-1 ring-red-200">
            <p className="break-keep text-sm font-medium text-red-600">
              입금 내역을 불러오지 못했습니다.
            </p>
            <Button variant="outline" className="mt-4" onClick={refresh}>
              다시 시도
            </Button>
          </div>
        )}

        {/* 빈 상태 — 미연동이면 랜딩형 인트로, 연동됨이면 기존 안내 (spec §4) */}
        {!loading && !error &&
          queue.actionNeeded.length === 0 &&
          queue.excluded.length === 0 &&
          queue.confirmed.length === 0 && (
            (accountStatus === 'unlinked' || accountStatus === 'in_progress') ? (
              <DepositsIntro
                accountStatus={accountStatus}
                onStartOnboarding={handleStartOnboarding}
              />
            ) : accountStatus === 'loading' ? null : (
              // loading 동안 '연동됨' 카드가 먼저 번쩍이는 오표시 방지 — 판정 전엔 아무것도 안 그림.
              <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-slate-200">
                <p className="break-keep font-semibold text-slate-700">아직 들어온 입금이 없습니다.</p>
                <p className="mt-1 break-keep text-sm text-slate-400">
                  연동된 계좌로 입금이 들어오면 자동 매칭됩니다.
                </p>
              </div>
            )
          )}

        {/* 확인 대기 */}
        {!loading && !error && queue.actionNeeded.length > 0 && (
          <section>
            <div className="mb-2.5 flex items-center gap-2">
              {/* 페이지 최강 라벨 — uppercase/tracking은 한글에 무효라 제거, amber는 DepositRow 대기 상태 색 언어 재사용 */}
              <h2 className="text-[15px] font-bold text-slate-900">
                확인 대기
              </h2>
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold tabular-nums text-amber-800">
                {queue.actionNeeded.length}
              </span>
            </div>
            <div className="space-y-2">
              {queue.actionNeeded.map((item) => (
                <DepositRow
                  key={item.match.id}
                  item={item}
                  queueVersion={queueVersion}
                  onConfirm={confirm}
                  onUndo={undo}
                  onReject={reject}
                  onLoadManualCandidates={loadManualCandidates}
                  highlighted={highlightId === item.match.id}
                  onRequestRefresh={() => { void refresh(); }}
                  onRescueRevalidate={rescueRevalidate}
                  onRescueConfirm={rescueConfirm}
                />
              ))}
            </div>
          </section>
        )}

        {/* key=userId: 유저 전환 시 리마운트로 훅·로컬 상태 동기 초기화 — 이전 셀러 데이터 1렌더 잔상과
            전환 중 in-flight 배치 결과의 교차 기록 차단 (codexloop PR C R1-P1-4) */}
        <PartialDepositSection
          key={user?.id ?? 'anon'}
          userId={user?.id ?? null}
          accountLinked={accountStatus === 'linked'}
          queueVersion={queueVersion}
          onRequestRefresh={() => { void refresh(); }}
        />

        {/* 처리 완료 (접힘) — 확정된 입금이 제외 건보다 자주 찾는 대상이라 위에(오너 2026-07-06). */}
        {!loading && !error && queue.confirmed.length > 0 && (
          <Accordion type="single" collapsible>
            <AccordionItem value="confirmed" className="rounded-xl border-none bg-white/60 px-4 ring-1 ring-slate-200/70">
              <AccordionTrigger className="text-sm font-medium text-slate-500 hover:no-underline">
                <span className="flex items-center gap-2">
                  처리 완료
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-slate-500">
                    {queue.confirmed.length}
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2 pb-1">
                  {queue.confirmed.map((item) => (
                    <DepositRow
                      key={item.match.id}
                      item={item}
                      queueVersion={queueVersion}
                      onConfirm={confirm}
                      onUndo={undo}
                      onLoadManualCandidates={loadManualCandidates}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        {/* 매칭 제외 (접힘) — 주문 매칭에서 제외한 입금. 복구하면 다시 확인 대기로. */}
        {!loading && !error && queue.excluded.length > 0 && (
          <Accordion type="single" collapsible>
            <AccordionItem value="excluded" className="rounded-xl border-none bg-white/60 px-4 ring-1 ring-slate-200/70">
              <AccordionTrigger className="text-sm font-medium text-slate-500 hover:no-underline">
                <span className="flex items-center gap-2">
                  매칭 제외
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-slate-500">
                    {queue.excluded.length}
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2 pb-1">
                  {queue.excluded.map((item) => (
                    <DepositRow
                      key={item.match.id}
                      item={item}
                      queueVersion={queueVersion}
                      onConfirm={confirm}
                      onUndo={undo}
                      onRestore={restore}
                      onLoadManualCandidates={loadManualCandidates}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        <OnboardingWizardContainer ref={wizardRef} onChanged={handleAccountChanged} />
      </div>
    </div>
  );
}

export default function Deposits() {
  return (
    <PaidPlanGate message="입금매칭은 스탠다드 플랜 이상부터 제공됩니다.">
      <DepositsContent />
    </PaidPlanGate>
  );
}
